import snakeCaseKeys from 'snakecase-keys';
import { restClient } from '../../client';
import { User, UserProfile } from '../../resources';

export enum OrgManagerRole {
  owner = 'owner',
  admin = 'admin',
  staff = 'staff',
}

export const enum OrgManagerStatus {
  active = 'active',
  inactive = 'inactive',
}

export const enum OrgManagerInviteStatus {
  invited = 'invited',
  accepted = 'accepted',
  revoked = 'revoked',
}

export type OrgManager = {
  orgId: string;
  user: User;
  profile?: UserProfile;
  role: OrgManagerRole;
  status: OrgManagerStatus;
  inviteStatus: OrgManagerInviteStatus;
};

export enum OrgManagerByTokenErrors {
  orgManagerNotFound = 'Org manager not found',
  invalidToken = 'Invalid token',
  expiredToken = 'Token expired',
}

export type OrgManagerByTokenResponse = {
  firstName: string;
  lastName: string;
  email: string;
  orgName: string;
  error?: string;
};

export type InviteOrgManagerInput = {
  email: string;
  role: OrgManagerRole;
  orgId: string;
  firstName: string;
  lastName: string;
};

export type AcceptOrgManagerInviteInput = {
  password: string;
  token: string;
};

export type HigherEdManagerDetails = {
  orgId: string;
  role: OrgManagerRole;
  userId: string;
  inviteStatus: OrgManagerInviteStatus;
  status: OrgManagerStatus;
};

export const sendInviteToOrgManager = async (data: InviteOrgManagerInput) => {
  return restClient.request<{ data: null }>('/api/v1/higher_ed/org_manager/invite/send',
    { method: 'POST', data: snakeCaseKeys(data) });
};

export const resendInviteToOrgManager = async (managerId: string) => {
  return restClient.request<{ data: Record<string, never>  }>(`/api/v1/higher_ed/org_manager/${managerId}/invite/resend`,
    { method: 'POST', data: {} });
};

export const getOrgManagerByInviteToken = async (token: string) => {
  return restClient.request<{ data: OrgManagerByTokenResponse }>(`/api/v1/view/higher_ed/org_manager/invite/by_token/${token}`,
    { method: 'GET' });
};

export const acceptOrgManagerInvite = async (data: AcceptOrgManagerInviteInput) => {
  return restClient.request<{ data: null }>('/api/v1/higher_ed/org_manager/invite/accept',
    { method: 'POST', data: snakeCaseKeys(data) });
};

export const getOrgManager = async (managerId: string ) => {
  return restClient.request < { data: OrgManager }>(`/api/v1/higher_ed/org_manager/${managerId}`,
    { method: 'GET' });
};

export const getOrgManagersByOrgId = async (orgId: string ) => {
  return restClient.request<{ data: OrgManager[] }>(`/api/v1/view/higher_ed/org_manager/by_org_id/${orgId}`,
    { method: 'GET' });
};

export const transferOwnership = async (managerId: string, role: Exclude<OrgManagerRole, OrgManagerRole.owner>) => {
  return restClient.request<{ data: null }>(`/api/v1/higher_ed/org_manager/${managerId}/transfer_ownership`,
    { method: 'POST', data: { role } });
};

export const promoteOrgManager = async (managerId: string) => {
  return restClient.request<{ data: null }>(`/api/v1/higher_ed/org_manager/${managerId}/promote`,
    { method: 'POST' });
};

export const demoteOrgManager = async (managerId: string) => {
  return restClient.request<{ data: null }>(`/api/v1/higher_ed/org_manager/${managerId}/demote`,
    { method: 'POST' });
};

export const deactivateOrgManager = async (managerId: string) => {
  return restClient.request<{ data: OrgManager }>(`/api/v1/higher_ed/org_manager/${managerId}/deactivate`,
    { method: 'POST' });
};

export const activateOrgManager = async (managerId: string) => {
  return restClient.request<{ data: OrgManager }>(`/api/v1/higher_ed/org_manager/${managerId}/activate`,
    { method: 'POST' });
};

export const revokeOrgManagerInvite = async (managerId: string) => {
  return restClient.request<{ data: OrgManager }>(`/api/v1/higher_ed/org_manager/${managerId}/invite/revoke`,
    { method: 'POST' });
};

export const reinviteOrgManager = async (managerId: string) => {
  return restClient.request<{ data: OrgManager }>(`/api/v1/higher_ed/org_manager/${managerId}/invite/reinvite`,
    { method: 'POST' });
};

export const getHigherEdUserProfile = async (userId: string) =>
  restClient.request<{ data: UserProfile }>(`/api/v1/higher_ed/user/${userId}/profile`,
    { method: 'GET' });
